/**
 * Export-file store backed by the workspace folder.
 *
 * Each project's completed render files live under
 * `projects/{id}/exports/{fileName}`. This module provides
 * CRUD operations scoped to a project.
 */

import { createLogger } from '@/shared/logging/logger'
import { requireWorkspaceRoot } from './root'
import {
  exists,
  listDirectory,
  readBlob,
  removeEntry,
  writeBlob,
} from './fs-primitives'
import { projectExportFilePath, projectExportsDir } from './paths'

const logger = createLogger('WorkspaceFS:Exports')

export interface ExportFileEntry {
  name: string
  size: number
  lastModified: number
}

/**
 * Save a rendered export blob to `projects/{projectId}/exports/{fileName}`.
 * De-duplicates filenames by appending ` (2)`, ` (3)`, etc.
 */
export async function saveExportFile(
  projectId: string,
  fileName: string,
  blob: Blob,
): Promise<string> {
  const root = requireWorkspaceRoot()
  const uniqueName = await resolveUniqueFileName(root, projectId, fileName)
  const segments = projectExportFilePath(projectId, uniqueName)
  await writeBlob(root, segments, blob)
  logger.info(`Saved export: ${segments.join('/')} (${blob.size} bytes)`)
  return uniqueName
}

/**
 * List all export files for a project, sorted newest-first.
 */
export async function listExportFiles(projectId: string): Promise<ExportFileEntry[]> {
  const root = requireWorkspaceRoot()
  const dirSegments = projectExportsDir(projectId)
  const entries = await listDirectory(root, dirSegments)

  const files: ExportFileEntry[] = []
  for (const entry of entries) {
    if (entry.kind !== 'file') continue
    if (entry.name.startsWith('.')) continue
    const blob = await readBlob(root, [...dirSegments, entry.name])
    if (!blob) continue
    const asFile = blob as File
    files.push({
      name: entry.name,
      size: asFile.size,
      lastModified: new Date(asFile.lastModified).getTime(),
    })
  }

  files.sort((a, b) => b.lastModified - a.lastModified)
  return files
}

/**
 * Read an export file as a Blob.
 */
export async function readExportFile(
  projectId: string,
  fileName: string,
): Promise<Blob | null> {
  const root = requireWorkspaceRoot()
  return readBlob(root, projectExportFilePath(projectId, fileName))
}

/**
 * Delete an export file.
 */
export async function deleteExportFile(
  projectId: string,
  fileName: string,
): Promise<void> {
  const root = requireWorkspaceRoot()
  await removeEntry(root, projectExportFilePath(projectId, fileName))
}

/**
 * Resolve a unique filename in the project's export directory.
 * Appends ` (2)`, ` (3)`, etc. when the name already exists.
 */
async function resolveUniqueFileName(
  root: FileSystemDirectoryHandle,
  projectId: string,
  fileName: string,
): Promise<string> {
  const dirSegments = projectExportsDir(projectId)
  const nameExists = async (name: string): Promise<boolean> => {
    return exists(root, [...dirSegments, name])
  }

  if (!(await nameExists(fileName))) {
    return fileName
  }

  const dot = fileName.lastIndexOf('.')
  const stem = dot > 0 ? fileName.slice(0, dot) : fileName
  const ext = dot > 0 ? fileName.slice(dot) : ''

  for (let i = 2; i < 100; i++) {
    const candidate = `${stem} (${i})${ext}`
    if (!(await nameExists(candidate))) {
      return candidate
    }
  }

  // Fallback: append timestamp
  return `${stem} (${Date.now()})${ext}`
}
